/**
 * Menu de contexto para mensagens do chat
 * 
 * Menu compacto e elegante com opções:
 * - Reagir (6 emojis rápidos estilo WhatsApp Web + picker completo)
 * - Responder
 * - Copiar texto
 * - Deletar (com diálogo de confirmação estilo WhatsApp)
 */

import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronDown, Reply, Copy, Trash2, Smile, RefreshCw, Plus, X, Forward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Message } from '@/pages/tenant/Chat';
import { QUICK_REACTION_EMOJIS } from '@/lib/emoji-data';
import { EmojiGrid } from '@/components/ui/emoji-grid';

// Tempo máximo para admin deletar qualquer mensagem (60 minutos)
const ADMIN_DELETE_LIMIT_MINUTES = 60;

// Tempo máximo para usuários normais deletarem suas mensagens (10 minutos)
const USER_DELETE_LIMIT_MINUTES = 10;

// Tempo limite do WhatsApp para deletar "para todos" (conservador)
const WHATSAPP_DELETE_LIMIT_MINUTES = 60;

// Tempo mínimo para considerar mensagem pending como "travada" (2 minutos)
const PENDING_TOO_LONG_MINUTES = 2;

interface MessageContextMenuProps {
  message: Message;
  /** Reação atual do agente nesta mensagem */
  currentReaction?: string;
  /** Callback para enviar reação */
  onReact: (emoji: string) => void;
  /** Callback para responder mensagem */
  onReply: () => void;
  /** Callback para deletar mensagem (deleteForEveryone: true = deleta WhatsApp + local, false = só local) */
  onDelete: (deleteForEveryone: boolean) => void;
  /** Callback para reenviar mensagem com falha */
  onResend?: () => void;
  /** Callback para encaminhar mensagem (abre Sheet de seleção de destinos) */
  onForward?: () => void;
  /** Se o menu está desabilitado */
  disabled?: boolean;
  /** ID do usuário logado atual (para verificar propriedade da mensagem) */
  currentUserId?: string;
  /** Indica se usuário é tenant admin (pode deletar qualquer mensagem até 1h) */
  isTenantAdmin?: boolean;
  /** Se a inbox é do Instagram (para ocultar reply em DMs) */
  isInstagramInbox?: boolean;
  /** Se a mensagem é um comentário do Instagram (comentários suportam reply) */
  isInstagramComment?: boolean;
}

/**
 * Verifica se a mensagem ainda pode ser deletada no WhatsApp
 * WhatsApp permite deletar "para todos" por ~1-2 horas
 */
const canDeleteOnWhatsApp = (sentAt: string): boolean => {
  const sentTime = new Date(sentAt).getTime();
  const now = Date.now();
  const diffMinutes = (now - sentTime) / (1000 * 60);
  return diffMinutes <= WHATSAPP_DELETE_LIMIT_MINUTES;
};

/**
 * Formata o tempo restante em minutos para exibição
 */
const formatTimeRemaining = (remainingMinutes: number): string => {
  if (remainingMinutes <= 0) return '';
  if (remainingMinutes < 1) return 'menos de 1 min';
  if (remainingMinutes < 60) return `${Math.floor(remainingMinutes)} min`;
  return `${Math.floor(remainingMinutes / 60)}h ${Math.floor(remainingMinutes % 60)}min`;
};

/**
 * Calcula tempo restante para deletar no WhatsApp
 */
const getWhatsAppDeleteTimeRemaining = (sentAt: string): string => {
  const sentTime = new Date(sentAt).getTime();
  const now = Date.now();
  const diffMinutes = (now - sentTime) / (1000 * 60);
  const remaining = WHATSAPP_DELETE_LIMIT_MINUTES - diffMinutes;
  return formatTimeRemaining(remaining);
};

/**
 * Verifica se o usuário pode deletar a mensagem
 * Regras:
 * - Tenant Admin: pode deletar qualquer mensagem até 1h após envio
 * - Demais usuários: só podem deletar suas próprias mensagens até 10min após envio
 */
const canDeleteMessage = (
  message: Message,
  currentUserId?: string,
  isTenantAdmin?: boolean
): { canDelete: boolean; reason?: string; timeRemaining?: string } => {
  const sentTime = new Date(message.sent_at).getTime();
  const now = Date.now();
  const diffMinutes = (now - sentTime) / (1000 * 60);

  // Tenant Admin: pode deletar qualquer mensagem até 1h
  if (isTenantAdmin) {
    if (diffMinutes > ADMIN_DELETE_LIMIT_MINUTES) {
      return {
        canDelete: false,
        reason: 'Prazo de 1 hora expirado'
      };
    }
    const remaining = ADMIN_DELETE_LIMIT_MINUTES - diffMinutes;
    return {
      canDelete: true,
      timeRemaining: formatTimeRemaining(remaining)
    };
  }

  // Demais usuários: só suas próprias mensagens até 10min
  const isOwnMessage = message.sender_id === currentUserId;

  if (!isOwnMessage) {
    return {
      canDelete: false,
      reason: 'Você só pode excluir suas próprias mensagens'
    };
  }

  if (diffMinutes > USER_DELETE_LIMIT_MINUTES) {
    return {
      canDelete: false,
      reason: 'Prazo de 10 minutos expirado'
    };
  }

  const remaining = USER_DELETE_LIMIT_MINUTES - diffMinutes;
  return {
    canDelete: true,
    timeRemaining: formatTimeRemaining(remaining)
  };
};

export function MessageContextMenu({
  message,
  currentReaction,
  onReact,
  onReply,
  onDelete,
  onResend,
  onForward,
  disabled = false,
  currentUserId,
  isTenantAdmin,
  isInstagramInbox = false,
  isInstagramComment = false,
}: MessageContextMenuProps) {
  const [open, setOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);

  // Verifica permissão de exclusão baseada no papel do usuário
  const deletePermission = canDeleteMessage(message, currentUserId, isTenantAdmin);

  // Handler para reenviar mensagem
  const handleResend = () => {
    onResend?.();
    setOpen(false);
  };

  // Handler para copiar texto
  const handleCopy = async () => {
    if (message.content) {
      try {
        await navigator.clipboard.writeText(message.content);
        toast.success('Texto copiado!');
      } catch {
        toast.error('Erro ao copiar texto');
      }
    }
    setOpen(false);
  };

  // Handler para reação (rápida ou do picker completo)
  const handleReact = (emoji: string) => {
    // Se clicou no mesmo emoji, remove a reação (passa vazio)
    const newEmoji = emoji === currentReaction ? '' : emoji;
    onReact(newEmoji);
    setOpen(false);
    setShowFullEmojiPicker(false);
  };

  // Handler para abrir picker completo de emojis
  const handleOpenFullPicker = () => {
    setOpen(false); // Fecha dropdown primeiro
    // Pequeno delay para garantir que o dropdown fechou
    setTimeout(() => {
      setShowFullEmojiPicker(true);
    }, 50);
  };

  // Handler para responder
  const handleReply = () => {
    setOpen(false);
    setTimeout(() => {
      onReply();
    }, 50);
  };

  // Handler para clique no botão deletar
  const handleDeleteClick = () => {
    setOpen(false);
    
    if (canDeleteWhatsApp) {
      setShowDeleteDialog(true);
    } else {
      onDelete(false);
    }
  };

  // Handler para "Apagar para todos" (WhatsApp + local)
  const handleDeleteForEveryone = () => {
    setShowDeleteDialog(false);
    onDelete(true);
  };

  // Handler para "Apagar para mim" (apenas local)
  const handleDeleteForMe = () => {
    setShowDeleteDialog(false);
    onDelete(false);
  };

  // Verifica se pode deletar no WhatsApp (apenas mensagens outbound com external_id)
  const isOutbound = message.direction === 'outbound';
  const hasExternalId = !!message.external_id;
  const canDeleteWhatsApp = isOutbound && hasExternalId && canDeleteOnWhatsApp(message.sent_at);
  const whatsAppTimeRemaining = isOutbound && hasExternalId ? getWhatsAppDeleteTimeRemaining(message.sent_at) : '';

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            className={cn(
              'p-1 rounded hover:bg-muted/80 transition-colors',
              'opacity-0 group-hover:opacity-100',
              'focus:outline-none focus:ring-1 focus:ring-ring'
            )}
            title="Opções"
          >
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="start" 
          side="bottom"
          className="w-44 bg-card border shadow-lg rounded-lg"
        >
          {/* Reenviar - para mensagens com falha OU pending há mais de 2 minutos */}
          {(() => {
            // Verifica se mensagem está pending há muito tempo (travada)
            const isPendingTooLong = message.status === 'pending' && 
              (Date.now() - new Date(message.sent_at).getTime()) > PENDING_TOO_LONG_MINUTES * 60 * 1000;
            
            const showResendButton = (message.status === 'failed' || isPendingTooLong) && onResend;
            
            if (!showResendButton) return null;
            
            return (
              <>
                <DropdownMenuItem 
                  onClick={handleResend} 
                  className="gap-2 cursor-pointer text-primary focus:text-primary"
                >
                  <RefreshCw className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span>Reenviar</span>
                    {isPendingTooLong && (
                      <span className="text-[10px] text-muted-foreground font-normal">
                        (mensagem travada)
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            );
          })()}

          {/* Reagir - 6 emojis rápidos + botão "+" estilo WhatsApp Web */}
          {message.external_id && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2 cursor-pointer">
                <Smile className="h-4 w-4" />
                <span>Reagir</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-2 min-w-0">
                <div className="flex items-center gap-1">
                  {/* 6 emojis rápidos */}
                  {QUICK_REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleReact(emoji)}
                      className={cn(
                        'h-8 w-8 flex items-center justify-center text-xl rounded',
                        'hover:bg-muted transition-colors',
                        emoji === currentReaction && 'bg-primary/20 ring-1 ring-primary'
                      )}
                      title={emoji === currentReaction ? 'Remover reação' : `Reagir com ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                  
                  {/* Botão "+" para abrir picker completo */}
                  <button
                    onClick={handleOpenFullPicker}
                    className="h-8 w-8 flex items-center justify-center rounded hover:bg-muted transition-colors"
                    title="Mais emojis"
                  >
                    <Plus className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Responder - Para Instagram DMs não exibe (API não suporta reply) */}
          {/* Para Instagram comentários exibe normalmente (usa fluxo de comentário público) */}
          {message.external_id && (!isInstagramInbox || isInstagramComment) && (
            <DropdownMenuItem onClick={handleReply} className="gap-2 cursor-pointer">
              <Reply className="h-4 w-4" />
              <span>Responder</span>
            </DropdownMenuItem>
          )}

          {/* Encaminhar — apenas para mensagens enviadas com sucesso e tipos suportados.
              Backend re-valida provider (WABA), janela 24h e tipo. */}
          {onForward && message.external_id && !isInstagramInbox && (
            ['text','image','audio','video','document','location','ptv'].includes(message.message_type)
          ) && (
            <DropdownMenuItem
              onClick={() => { setOpen(false); setTimeout(() => onForward(), 50); }}
              className="gap-2 cursor-pointer"
            >
              <Forward className="h-4 w-4" />
              <span>Encaminhar</span>
            </DropdownMenuItem>
          )}

          {/* Copiar texto - apenas se tiver conteúdo de texto */}
          {message.content && (
            <DropdownMenuItem onClick={handleCopy} className="gap-2 cursor-pointer">
              <Copy className="h-4 w-4" />
              <span>Copiar texto</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Deletar - Só exibe se usuário tem permissão */}
          {deletePermission.canDelete ? (
            <DropdownMenuItem 
              onClick={handleDeleteClick} 
              className="gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              <div className="flex flex-col">
                <span>Deletar</span>
                {isOutbound && hasExternalId && !canDeleteWhatsApp && (
                  <span className="text-[10px] text-muted-foreground font-normal">
                    (somente local)
                  </span>
                )}
                {deletePermission.timeRemaining && (
                  <span className="text-[10px] text-muted-foreground font-normal">
                    ({deletePermission.timeRemaining} restantes)
                  </span>
                )}
              </div>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem 
              disabled
              className="gap-2 cursor-not-allowed opacity-50"
              title={deletePermission.reason}
            >
              <Trash2 className="h-4 w-4" />
              <div className="flex flex-col">
                <span>Deletar</span>
                <span className="text-[10px] text-muted-foreground font-normal">
                  {deletePermission.reason}
                </span>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog com picker completo de emojis para reações */}
      <Dialog open={showFullEmojiPicker} onOpenChange={setShowFullEmojiPicker}>
        <DialogContent className="max-w-md p-0 gap-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-base font-medium">Escolher reação</DialogTitle>
          </DialogHeader>
          <EmojiGrid 
            onSelect={handleReact}
            selectedEmoji={currentReaction}
            columns={7}
            height="280px"
            showSearch={true}
            showCategories={true}
            footerAction={
              currentReaction ? (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleReact('')}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remover reação
                </Button>
              ) : undefined
            }
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação de exclusão estilo WhatsApp */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-[320px] gap-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">
              Deseja apagar a mensagem?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm">
              {whatsAppTimeRemaining && `Tempo restante no WhatsApp: ${whatsAppTimeRemaining}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary/10 hover:text-primary"
              onClick={handleDeleteForEveryone}
            >
              Apagar para todos
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={handleDeleteForMe}
            >
              Apagar para mim
            </Button>
            
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancelar
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
